import WebinarApp from "./components/WebinarApp";
import { userDataMapping, WEBINAR_REG_MESSAGES } from "./consts";
import {
    getLightboxNameFromSelector,
    getRegStatusClientSide,
    registerUserForWebinar,
    sendRegistrantToIterable,
    setRegStatusClientSide,
    setFormVisibility,
    checkZephrUserExists,
} from "./utils";

async function initConvertrFormForWebinar () {
    const BlaizeSDK = window.BlaizeSDK;
    const isUserLoggedIn = !!Object.entries(window.awGetUserData?.() || {})
        .length;
    const containerSelector = '#digioh-form-wrapper';

    const setRegisteredEmail = (args) => {

        if (typeof window?.adw?.webinar_app?.setRegisteredEmail === 'function') {
            window.adw.webinar_app.setRegisteredEmail(args);
        }
    };

    const setHasZephrUser = (val) => {
        if (typeof window?.adw?.webinar_app?.setHasZephrUser === 'function') {
            window.adw.webinar_app.setHasZephrUser(val);
        }
    };

    const setFormFields = (val) => {
        if (typeof window?.adw?.webinar_app?.setFormFields === 'function') {
            window.adw.webinar_app.setFormFields(val);
        }
    };

    const setShowPasswordCreation = (val) => {
        if (typeof window?.adw?.webinar_app?.setShowPasswordCreation === 'function') {
            window.adw.webinar_app.setShowPasswordCreation(val);
        }
    };

    const setIsLoggedIn = (val) => {
        if (typeof window?.adw?.webinar_app?.setIsLoggedIn === 'function') {
            window.adw.webinar_app.setIsLoggedIn(val);
        }
    };

    const setRegFormIsVisible = (val) => {
        if (typeof window?.adw?.webinar_app?.setRegFormIsVisible === 'function') {
            window.adw.webinar_app.setRegFormIsVisible(val);
        }
    };

    const setSubmittingForm = (val) => {
        if (typeof window?.adw?.webinar_app?.setSubmittingForm === 'function') {
            window.adw.webinar_app.setSubmittingForm(val);
        }
    };

    try {
        const email = await getEmail();

        const emailFromClientSide = getRegStatusClientSide();

        if (emailFromClientSide) {
            setRegisteredEmail({
                email: emailFromClientSide,
                message: WEBINAR_REG_MESSAGES.existing,
            });
            adw.webinar_meta.is_registered = true;
        } else if (email) {
            setIsLoggedIn(true);

            try {
                const registrationStatus = await getRegistrationStatus({
                    email: email,
                });
                if (registrationStatus) {
                    setRegisteredEmail({
                        email: email,
                        message: WEBINAR_REG_MESSAGES.existing,
                    });
                    adw.webinar_meta.is_registered = true;
                } else {
                    loadForm();
                }
            } catch (err) {
                console.log("error getting registration status", err);
                throw err;
            }
        } else {
            // Not Registered
            // Register User on Zephr and on Webcasts
            adw.can_register_on_zephr = true;

            loadForm();
        }
    } catch (err) {
        console.error("error getting email", err);
        loadForm();
    }

    async function getEmail () {
        return new Promise((resolve, reject) => {
            adw.is_signed_in = isUserLoggedIn || false;

            if (!isUserLoggedIn || !BlaizeSDK) {
                return resolve(false);
            }
            BlaizeSDK.getAccount((error, account) => {
                if (error) {
                    console.error("BlaizeSDK. Failed to pull the user account:", error);
                    return reject(error);
                }

                const userEmailAddress = account[ "identifiers" ][ "email_address" ];

                resolve(userEmailAddress);
            });
        });
    }

    function getRegistrationStatus ({ email }) {
        return new Promise((resolve, reject) => {

            if (!email) {
                return reject('No email provided to check registration status.');
            }

            if (adw?.webinar_meta?.registration_url) {

                const queryParams = new URLSearchParams();
                const postId = Number(adw?.post?.ID || 0);

                if (postId) {
                    queryParams.set('post_id', postId);
                } else {
                    queryParams.set('registration_url', adw.webinar_meta.registration_url);
                }

                queryParams.set('email', email);

                fetch(`${window.location.origin}/wp-json/adw_webinar_registration/v1/registration/?${queryParams.toString()}`)
                    .then(response => {
                        if (response.status !== 200) {
                            return reject();
                        }
                        return response.json();
                    })
                    .then(result => {
                        const isRegistered = !!result?.joinUrl || !!result?.autoJoinUrl || !!result?.is_registered;

                        if (isRegistered) {
                            setRegStatusClientSide(email);
                            return resolve(true);
                        }

                        return reject(result);
                    })
                    .catch(error => reject(error));

                return;
            }

            fetch(`${window.location.origin}/wp-json/adw-webcasts/v1/check_registration_status/?email=${email}&event_id=${adw.webinar_meta.event_id}`)
                .then(response => {
                    if (response.status !== 200) {
                        return reject();
                    }
                    return response.json();
                })
                .then(result => {
                    if (result?.is_registered) {

                        setRegStatusClientSide(email);

                        return resolve(result?.is_registered);
                    }
                    return reject(result);
                })
                .catch(error => reject(error));

        });

    }

    if (!adw?.webinar_meta?.event_id && !adw?.webinar_meta?.registration_url) {
        console.error('Event ID not found. Could not complete registration check.');
    }

    function getCurrentSlug () {
        const pathname = window.location.pathname;
        const parts = pathname.split("/").filter(Boolean);
        return parts.pop() || null;
    }

    /**
     * Updates the user's Zephr profile with the provided field values, but only if they differ from the existing values.
     * @param {Record<string, string>} payload the values to update on user's Zephr profile
     * @returns {void}
     */
    const updateFieldValuesOnZephr = (payload) => {
        if (!isUserLoggedIn || !BlaizeSDK) {
            return;
        }

        /**
         * Callback function to update the profile with the new values, after comparing them with the existing values to avoid unnecessary updates.
         * @param {Record<string, string>} savedProfileDetails the current user's profile details pulled from Zephr
         * @returns {void}
         */
        const update = (savedProfileDetails) => {
            // Removing entries with unchanged values
            const filteredPayload = Object.fromEntries(
                Object.entries(payload).filter(([ key, value ]) => savedProfileDetails[ key ] !== value)
            );

            // Do nothing if nothing changed
            if (!Object.entries(filteredPayload).length) {
                return;
            }

            // Updating the profile
            BlaizeSDK.updateProfile(
                filteredPayload,
                true, // keep true
                (error) => {
                    if (error) {
                        console.error("Failed to update profile details", error);
                        return;
                    }
                    console.log("Profile details has been updated successfully");
                },
            );
        };

        // Pulling current user profile
        BlaizeSDK.getProfile((error, dataFromProfile) => {
            if (error) {
                console.error("BlaizeSDK. Failed to pull the user profile", error);
                return;
            }

            update(dataFromProfile);
        });
    };

    const sendDataToParsely = () => {
        if (window?.PARSELY?.conversions?.trackLeadCapture) {
            window.PARSELY.conversions.trackLeadCapture("Webinar_Reg");

            console.log("Track window.parent.PARSELY");
        }
    };

    /**
     * Pre-populates the form with the user data
     * @param {HTMLIFrameElement|undefined} [iframeNode=undefined] - The iframe node to pre-populate
     * @returns {void}
     */
    function populate (iframeNode = undefined) {
        if ((!isUserLoggedIn || !BlaizeSDK) && !iframeNode) return;

        BlaizeSDK?.getAccount((error, account) => {
            if (error) {
                console.error("BlaizeSDK. Failed to pull the user account:", error);
                return;
            }

            const userEmail = account[ "identifiers" ][ "email_address" ];

            BlaizeSDK?.getProfile((error, userProfile) => {
                if (error) {
                    console.error("BlaizeSDK. Failed to pull the user profile:", error);
                    return;
                }

                userDataMapping.forEach(({ userProfileKey, formInputId }) => {
                    const field = iframeNode.contentDocument.querySelector(
                        `#${formInputId}`,
                    );

                    if (!field) return;

                    if (userProfileKey === "email") {
                        field.value ||= userEmail;
                        field.setAttribute("disabled", "true");
                        field.style.opacity = 0.5;
                        field.style.cursor = "not-allowed";
                        return;
                    }

                    if (!(userProfileKey in userProfile)) return;

                    const value = userProfile[ userProfileKey ];

                    if (
                        userProfileKey === "country" &&
                        window.ZEPHR_COUNTRY_VALUE_DICTIONARY &&
                        value in window.ZEPHR_COUNTRY_VALUE_DICTIONARY
                    ) {
                        field.value ||= window.ZEPHR_COUNTRY_VALUE_DICTIONARY[ value ];
                        field.dispatchEvent(new Event("change", { bubbles: true }));
                        return;
                    }

                    if (userProfileKey === "state" &&
                        window.ZEPHR_US_STATES_VALUE_DICTIONARY &&
                        value in window.ZEPHR_US_STATES_VALUE_DICTIONARY) {
                        field.value ||= window.ZEPHR_US_STATES_VALUE_DICTIONARY[ value ];
                        field.dispatchEvent(new Event("change", { bubbles: true }));
                        return;
                    }

                    field.value ||= value;
                });
            });
        });
    }

    /**
     * Loads the form, and adds event listeners to handle the form submission and display of the form.
     * @returns {void}
     */
    function loadForm () {
        const container = document.querySelector(containerSelector);
        if (!container) {
            console.error("Container not found");
            return;
        }

        const form = document.createElement("div");
        form.id = "lightbox-inline-webinar-form";
        form.style.textAlign = 'center';
        form.style.opacity = '0';
        form.style.transition = 'opacity 100ms ease-in-out';
        container.prepend(form);

        const abortController = new AbortController();
        const { signal } = abortController;

        // Listen for messages from the DIGIOH Custom JS
        window.addEventListener(
            "message",
            ({ data }) => {
                if (data?.type === "iframe-loaded") {
                    setRegFormIsVisible(true);
                    const placeholder = document.getElementById('digioh-form-placeholder');
                    if (placeholder) {
                        placeholder.style.opacity = '0';
                        placeholder.addEventListener('transitionend', () => placeholder.remove(), { once: true });
                    }
                    window.DIGIOH_API.clearDataLayer();

                    // Dispatch event for GA4 plugin
                    document
                        .querySelector(containerSelector)
                        .dispatchEvent(new Event("aw_convertr_form_loaded"));

                    populate(
                        document
                            .querySelector(containerSelector)
                            ?.querySelector?.("iframe"),
                    );
                    return;
                }

                if (data?.type === "form-submit-data") {
                    setSubmittingForm(true);
                    handleSubmit(data.payload)
                        .finally(() => {
                            setSubmittingForm(false);
                            setRegFormIsVisible(false);
                            abortController.abort();
                        });
                }
            },
            { signal },
        );
    }

    /**
     * Handles the form submission and display of the form.
     * @param {Record<string, string>} payload - The payload of the form submission.
     * @returns {Promise<void>}
     */
    async function handleSubmit (payload) {
        // Dispatch event for GA4 plugin
        document
            .querySelector(containerSelector)
            .dispatchEvent(new Event("aw_convertr_form_submitted"));

        /**
         * Gets the code corresponding to a value from zephr country and state dictionaries
         * @param {string} value - The value to look up
         * @param {Record<string, string>} dictionary - The dictionary to search
         * @returns {string|undefined} The corresponding code, or undefined if not found
         */
        const getCodeFromValue = (value = "", dictionary = {}) => {
            if (!value || Object.entries(dictionary).length === 0) {
                return undefined;
            }
            const entry = Object.entries(dictionary).find(
                ([ , name ]) => name === value,
            );
            return entry?.[ 0 ];
        };

        /**
         * Zephr's business-type attribute schema (GET /v3/schema/users/business-type) expects the
         * exact label text as the attribute value, not the numeric codes from AW_Helpers::get_business_types()
         * (that list belongs to a separate, legacy registration system). DIGIOH's dropdown text matches
         * Zephr's valid values verbatim, except its bare "OTHER" option, which Zephr's schema lists as "Other".
         * @param {string} value - The raw DIGIOH business-type label
         * @returns {string|undefined} The Zephr-valid value, or undefined if empty
         */
        const getBusinessTypeValue = (value = "") => {
            if (!value) {
                return undefined;
            }
            return value.toLowerCase() === "other" ? "Other" : value;
        };

        const formattedPayload = {
            "first-name": payload.custom_1,
            "last-name": payload.custom_2,
            company: payload.custom_4,
            title: payload.custom_8,
            "job-function": payload.custom_7,
            "job-levelseniority": payload.custom_6,
            phone: payload.custom_12,
        };

        // Only include business-type, country and state if they resolve to a Zephr-valid value
        if (payload.custom_5) {
            const businessTypeValue = getBusinessTypeValue(payload.custom_5);
            if (businessTypeValue) {
                formattedPayload[ "business-type" ] = businessTypeValue;
            }
        }
        if (`${payload.custom_9}` !== "") {
            const countryCode = getCodeFromValue(
                payload.custom_9,
                window.ZEPHR_COUNTRY_VALUE_DICTIONARY,
            );
            if (countryCode) {
                formattedPayload.country = countryCode;
            }
        }
        if (`${payload.custom_11}` !== "") {
            const stateCode = getCodeFromValue(
                payload.custom_11,
                window.ZEPHR_US_STATES_VALUE_DICTIONARY,
            );
            if (stateCode) {
                formattedPayload.state = stateCode;
            }
        }

        const payloadExtra = {
            gdpr_adweek: payload.custom_14,
            gdpr_partners: payload.custom_14,
        };

        if (payload.custom_10) {
            payloadExtra.customField1 = payload.custom_10;
        }
        if (payload.custom_13) {
            payloadExtra.customField2 = payload.custom_13;
        }
        if (payload.custom_17) {
            payloadExtra.customField3 = payload.custom_17;
        }

        sendDataToParsely();

        if (!payload.custom_3) {
            return;
        }

        const email = payload.custom_3;
        let zephrUserExistsPromise = null;

        if (window.adw.can_register_on_zephr) {
            setFormFields(formattedPayload);
            zephrUserExistsPromise = checkZephrUserExists(email)
                .then((exists) => ({ exists }))
                .catch((error) => ({ error }));
        } else {
            // Update values on Zephr
            updateFieldValuesOnZephr(formattedPayload);
            setHasZephrUser(true);
        }

        const getRegistrantData = () => {
            const urlQuery = new URLSearchParams(window.location.search);
            const sti = urlQuery.get("sti") || "";

            const eventIdFromMeta = adw?.webinar_meta?.event_id || null;
            const eventIdFromRegistrationURL = adw?.webinar_meta?.registration_url ? adw.webinar_meta.registration_url.split('/').filter(Boolean).pop() : null;

            return {
                email,
                ei: eventIdFromMeta || eventIdFromRegistrationURL,
                fname: formattedPayload[ "first-name" ],
                lname: formattedPayload[ "last-name" ],

                company: formattedPayload.company,
                title: formattedPayload.title,
                state: formattedPayload?.state || "",
                country: formattedPayload?.country || "",

                phone: formattedPayload.phone,

                Business_Type: formattedPayload[ "business-type" ] || "",
                Job_Function: formattedPayload[ "job-function" ],
                Job_Level: formattedPayload[ "job-levelseniority" ],

                // Send extra field data
                ...payloadExtra,
                sti: sti,
            };
        };

        // Register user on webinar platform
        let webinarRegistrationSucceeded = false;
        try {

            const registrationResponse = await registerUserForWebinar(getRegistrantData());

            const hasRegistrationUrl = !!adw?.webinar_meta?.registration_url;

            if (hasRegistrationUrl) {

                if (registrationResponse?.errors) {
                    alert("Error in registration: " + registrationResponse.errors.join(", "));
                }

                if (registrationResponse?.joinUrl) {
                    setRegisteredEmail({ email, skipUserExistenceCheck: true });
                    setRegStatusClientSide(email);
                    webinarRegistrationSucceeded = true;
                }

            } else {
                const isAlreadyRegistered = registrationResponse?.includes('User already registered');
                const isSuccess = registrationResponse?.includes('SUCCESS');


                if (isAlreadyRegistered) {
                    setRegisteredEmail({ email, message: WEBINAR_REG_MESSAGES.existing, skipUserExistenceCheck: true });
                    setRegStatusClientSide(email);
                    webinarRegistrationSucceeded = true;
                } else if (isSuccess) {
                    setRegisteredEmail({ email, message: WEBINAR_REG_MESSAGES.success, skipUserExistenceCheck: true });
                    setRegStatusClientSide(email);
                    webinarRegistrationSucceeded = true;
                } else {
                    alert(`Error in Webinars.net registration: ${JSON.stringify(registrationResponse)}`);
                    setFormVisibility(true);
                }
            }
        } catch (err) {
            setFormVisibility(true);
            console.error("Error in Webinar Registration", err);
        }

        if (webinarRegistrationSucceeded && zephrUserExistsPromise) {
            const { exists, error } = await zephrUserExistsPromise;
            if (error) {
                setShowPasswordCreation(true);
                console.error("Error checking Zephr User existence", error);
            } else if (exists) {
                setHasZephrUser(true);
            } else {
                setShowPasswordCreation(true);
            }
        }

        // Send the registered user to Iterable
        try {
            const formId = getCurrentSlug();
            const boxName = getLightboxNameFromSelector(formId);
            const sponsorName = (boxName || "").split("_")[ 0 ] || "Unknown";

            await sendRegistrantToIterable({
                eventName: adw?.post?.title,
                sponsorName,
                ...getRegistrantData(),
            });

        } catch (err) {
            console.log("Error in webinar platform (to Iterable)", err);
        }

    }
}

document.addEventListener(
    "DOMContentLoaded",
    () => {
        window.addEventListener(
            "adw.webinar_app.ready",
            initConvertrFormForWebinar,
            { once: true },
        );

        const elAppRoot = document.getElementById("webinar-reg-app");
        if (elAppRoot) {
            const ReactDOM = window.ReactDOM;
            ReactDOM.client.createRoot(elAppRoot).render(<WebinarApp />);
        }
    },
    { once: true }
);

window.initConvertrFormForWebinar = initConvertrFormForWebinar;
